import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../../navigation/routeConfig";
import { BODY_FONT, cardSx, DISPLAY_FONT, INK, INK_SOFT, PRIMARY } from "../dashboardTokens";

interface HealthRow {
  label: string;
  ok: boolean;
  detail: string;
  path: string;
}

interface AccountHealthCardProps {
  pharmacyVerified: boolean;
  pharmacyStatusLabel: string;
  bankingVerified: boolean;
  bankingLabel: string;
}

export const AccountHealthCard: React.FC<AccountHealthCardProps> = ({
  pharmacyVerified,
  pharmacyStatusLabel,
  bankingVerified,
  bankingLabel,
}) => {
  const navigate = useNavigate();

  const rows: HealthRow[] = [
    {
      label: "Pharmacy verification",
      ok: pharmacyVerified,
      detail: pharmacyStatusLabel,
      path: routeConfigs.pharmacyProfile.path,
    },
    {
      label: "Banking / payouts",
      ok: bankingVerified,
      detail: bankingLabel,
      path: routeConfigs.bankAccount.path,
    },
    {
      label: "DSCSA readiness",
      ok: pharmacyVerified,
      detail: pharmacyVerified ? "Marketplace access active" : "Complete profile verification",
      path: routeConfigs.pharmacyProfile.path,
    },
  ];

  return (
    <Box sx={{ ...cardSx, p: 2.25 }}>
      <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 16, color: INK, mb: 0.5 }}>
        Account Health
      </Typography>
      <Typography sx={{ fontFamily: BODY_FONT, fontSize: 13, color: INK_SOFT, mb: 1.5 }}>
        Quick verification and compliance snapshot
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {rows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: 1.25,
              borderRadius: "8px",
              bgcolor: row.ok ? "rgba(46, 125, 50, 0.06)" : "rgba(230, 81, 0, 0.06)",
            }}
          >
            {row.ok ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#2E7D32", mt: 0.15 }} />
            ) : (
              <ErrorOutlineIcon sx={{ fontSize: 20, color: "#E65100", mt: 0.15 }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: BODY_FONT, fontWeight: 600, fontSize: 13, color: INK }}>
                {row.label}
              </Typography>
              <Typography sx={{ fontFamily: BODY_FONT, fontSize: 12, color: INK_SOFT }}>
                {row.detail}
              </Typography>
            </Box>
            {!row.ok && (
              <Button
                size="small"
                onClick={() => navigate(row.path)}
                sx={{ textTransform: "none", fontSize: 12, fontWeight: 600, color: PRIMARY, minWidth: 0 }}
              >
                Fix
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AccountHealthCard;
